/**
 * Class representing a media track in the player.
 * @property {string} id - Unique identifier for the track
 * @property {string} name - Human-readable name
 * @property {string} kind - Track type (e.g., "audio", "video")
 * @property {string} participantType - Role of participant (e.g., "proctor", "student")
 * @property {Object} webrtcRef - Reference to WebRTC media element or track
 * @property {Object} hls - HLS streaming config or instance
 * @property {string} status - Current track status (e.g., "active", "paused")
 */
export default class SessionTrack {
  constructor({
    id = '',
    name = '',
    kind = '',
    participantType = '',
    participantSid = '',
    webrtcRef = null,
    hls = null,
    status = '',
  } = {}) {
    this.id = id;
    this.name = name;
    this.kind = kind;
    this.participantType = participantType;
    this.participantSid = participantSid;
    this.webrtcRef = webrtcRef;
    this.hls = hls;
    this.status = status;
  }

  /**
   * Populate an existing Track instance from a LiveKit publication.
   * @param {SessionTrack} trackInstance - existing Track to populate
   * @param {{participant: object, publication: object}} trackRef
   * @returns {SessionTrack}
   */
  populateFromLiveKit(trackRef) {
    const { participant, publication } = trackRef;
    const id = publication.trackSid;
    const name = publication.trackName || publication.trackSid;
    const kind = publication.kind;
    const source = publication.source;
    this.id = id;
    this.name = name;
    this.kind = kind;
    this.source = source;
    this.participantType = participant?.participantInfo?.attributes?.type;
    this.participantSid =
      participant?.participantInfo?.attributes?.participantSid;
    this.webrtcRef = trackRef;
    return this;
  }

  /**
   * Populate an existing Track instance from a HLS track.
   * @param {{participant: object, track: object}} trackObj
   * @returns {SessionTrack}
   */
  populateFromHls(trackObj) {
    const { participant, track } = trackObj;
    const id = track.trackId;
    const name = track?.info?.name || track.trackId;
    const kind = track.kind;
    const source = track.source;
    this.id = id;
    this.name = name;
    this.kind = kind;
    this.source = source;
    this.participantType = participant?.type;
    this.participantSid = participant?.sid;
    this.hls = trackObj;
    return this;
  }
}
